import React, { useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core";
import VisibilitySensor from "react-visibility-sensor";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { AiFillWechat } from "react-icons/ai";
import emailjs from "emailjs-com";

const ipapi = require("ipapi.co");

const useStyles = makeStyles((theme) => ({
  root: {
    background: "#f5dd3a",
    padding: "3rem 2rem 0rem",
    transition: "all .3s smooth",
  },
  contactText: {
    fontWeight: "600",
  },
  sideText: {
    fontWeight: "500",
  },
  contactMsg: {
    [theme.breakpoints.up("sm")]: {
      width: "40%",
      padding: "5.2rem !important",
    },
  },
  formDiv: {
    flex: 1,
    [theme.breakpoints.up("sm")]: {
      width: "40%",
    },
    borderRadius: "5px",
  },
  nameEmailDiv: {
    paddingTop: "1rem",
    [theme.breakpoints.up("sm")]: {
      display: "flex",
    },
  },
  nameInput: {
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("sm")]: {
      flexGrow: ".5",
    },
  },
  mobileInput: {
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("sm")]: {
      flexGrow: ".5",
    },
  },
  emailInput: {
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("sm")]: {
      flexGrow: ".5",
    },
  },
  cssOutlinedInput: {
    color: "#1c4c99",
    fontWeight: 700,
    "&$cssFocused $notchedOutline": {
      borderColor: `${theme.palette.primary.main} !important`,
    },
  },
  notchedOutline: {
    borderWidth: "1px",
    borderColor: "#1c4c99 !important",
  },
  submitBtn: {
    width: "fit-content",
    padding: "1rem",
    cursor: "pointer",
    border: "1px solid",
    borderRadius: "5px",
    margin: "1rem auto",
    background: "#1c4c99",
    color: "#f5dd3a",
    fontWeight: "800",
    fontSize: "1.1rem",
    transition: "all .3s ease",
    "&:hover": {
      color: "#1c4c99",
      background: "#f5dd3a",
      boxShadow: "0px 0px 10px 2px #1c4c99",
    },
  },
  error: {
    color: "#ff0800",
  },
}));

const initialMailStatus = {
  color: "#f5dd3a",
  message: "Submit",
};

const ContactContainer = ({ refProp, setRefInView }) => {
  const classes = useStyles();
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [services, setServices] = useState("");
  const [message, setMessage] = useState("");
  const [nameError, setNameError] = useState("");
  const [mobileError, setMobileError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [servicesError, setServicesError] = useState("");
  const [messageError, setMessageError] = useState("");
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);
  const [location, setLocation] = useState({});
  const [mailLimit, setMailLimit] = useState(4);
  const [mailStatus, setMailStatus] = useState(initialMailStatus);

  const timeoutRef = useRef(null);

  useEffect(() => {
    ipapi.location((loc) => setLocation(loc));
  }, []);

  useEffect(() => {
    if (mailLimit < 0) {
      setMailStatus({
        color: "#ff0800",
        message: "Your mailing queue is full.",
      });
      setIsSubmitButtonDisabled(true);
    }
  }, [mailLimit]);

  const handleSubmit = () => {
    if (!name) setNameError("Name is Required");
    if (!email) setEmailError("Email is Required");
    if (!services) setServicesError("Services is Required");
    if (!message) setMessageError("Message is Required");

    if (
      name &&
      email &&
      services &&
      message &&
      /^\w+@[a-zA-Z_]+?\.[a-zA-Z]{1,}$/.test(email)
    ) {
      setIsSubmitButtonDisabled(true);
      setMailStatus({
        color: "#55ff00",
        message: "Sending...",
      });
      var loc = {};
      if (
        !location.ip ||
        !location.city ||
        !location.region ||
        !location.country_name ||
        !location.org
      )
        loc = {};
      else
        loc = {
          "IP Address": location.ip,
          City: location.city,
          Region: location.region,
          Country: location.country_name,
          "Internet Service": location.org,
        };
      emailjs
        .send(
          process.env.REACT_APP_SERVICE_ID,
          process.env.REACT_APP_TEMPLATE_ID,
          {
            name,
            mobile: mobile ? mobile : "Not provided",
            email,
            services,
            message,
            location: JSON.stringify(loc, null, 2),
          },
          process.env.REACT_APP_USER_ID
        )
        .then(
          () => {
            setMailStatus({
              color: "#55ff00",
              message: "Message Sent!",
            });
            setMailLimit((prev) => prev - 1);

            if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);

            timeoutRef.current = setTimeout(() => {
              timeoutRef.current = null;
              if (mailLimit > 0) {
                setMailStatus(initialMailStatus);
                setIsSubmitButtonDisabled(false);
              }
            }, 5000);

            setName("");
            setMobile("");
            setEmail("");
            setServices("");
            setMessage("");
            setNameError("");
            setMobileError("");
            setEmailError("");
            setServicesError("");
            setMessageError("");
          },
          (err) => {
            // console.log(err);
            setMailStatus(() => {
              return {
                color: "#ff0800",
                message: "Message was not sent... Try again later",
              };
            });
            if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);

            timeoutRef.current = setTimeout(() => {
              timeoutRef.current = null;
              setMailStatus(initialMailStatus);
              setIsSubmitButtonDisabled(false);
            }, 2000);
          }
        );
    } else {
      setEmailError("Please enter a valid Email address");
    }
  };

  return (
    <VisibilitySensor
      partialVisibility
      // minTopValue={100}
      onChange={(isVisible) => isVisible && setRefInView("contact")}
    >
      {({ isVisible }) => (
        <div
          style={{
            opacity: `${isVisible ? "1" : "0.25"}`,
          }}
          ref={refProp}
          className={classes.root}
        >
          <Typography variant="h4" className={classes.contactText}>
            {"Contact Me"}
          </Typography>
          <div className={classes.contact}>
            <Grid container spacing={3}>
              <Grid item className={classes.contactMsg}>
                <AiFillWechat style={{ fontSize: "7rem" }} />
                <Typography variant="h6" className={classes.sideText}>
                  {
                    "Please let me know the services required. I am just a click away."
                  }
                </Typography>
              </Grid>
              <Grid item className={classes.formDiv}>
                <form onSubmit={() => console.log("123")}>
                  <div className={classes.nameEmailDiv}>
                    <div className={classes.nameInput}>
                      <TextField
                        label="Name"
                        variant="outlined"
                        color="primary"
                        required
                        value={name}
                        onChange={(e) => {
                          setName(e.target.value);
                          if (name) setNameError("");
                        }}
                        InputLabelProps={{
                          style: {
                            color: "#1c4c99",
                          },
                        }}
                        InputProps={{
                          classes: {
                            root: classes.cssOutlinedInput,
                            focused: classes.cssFocused,
                            notchedOutline: classes.notchedOutline,
                          },
                        }}
                      />
                      {nameError && (
                        <label className={classes.error}>{nameError}</label>
                      )}
                    </div>
                    <div style={{ padding: "1rem" }} />
                    <div className={classes.mobileInput}>
                      <TextField
                        label="Mobile Number"
                        variant="outlined"
                        color="primary"
                        type="text"
                        value={mobile}
                        onChange={(e) => {
                          setMobile(e.target.value);
                        }}
                        InputLabelProps={{
                          style: {
                            color: "#1c4c99",
                          },
                        }}
                        InputProps={{
                          classes: {
                            root: classes.cssOutlinedInput,
                            focused: classes.cssFocused,
                            notchedOutline: classes.notchedOutline,
                          },
                        }}
                      />
                      {mobileError && (
                        <label className={classes.error}>{mobileError}</label>
                      )}
                    </div>
                  </div>
                  <div style={{ padding: "1rem" }} />
                  <div className={classes.emailInput}>
                    <TextField
                      label="Email"
                      variant="outlined"
                      color="primary"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (email) setEmailError("");
                      }}
                      required
                      InputLabelProps={{
                        style: {
                          color: "#1c4c99",
                        },
                      }}
                      InputProps={{
                        classes: {
                          root: classes.cssOutlinedInput,
                          focused: classes.cssFocused,
                          notchedOutline: classes.notchedOutline,
                        },
                      }}
                    />
                    {emailError && (
                      <label className={classes.error}>{emailError}</label>
                    )}
                  </div>
                  <div style={{ padding: "1rem" }} />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <TextField
                      label="Services Required"
                      variant="outlined"
                      color="primary"
                      multiline
                      required
                      fullWidth
                      rows={3}
                      value={services}
                      onChange={(e) => {
                        setServices(e.target.value);
                        if (services) setServicesError("");
                      }}
                      InputLabelProps={{
                        style: {
                          color: "#1c4c99",
                        },
                      }}
                      InputProps={{
                        classes: {
                          root: classes.cssOutlinedInput,
                          focused: classes.cssFocused,
                          notchedOutline: classes.notchedOutline,
                        },
                      }}
                    />
                    {servicesError && (
                      <label className={classes.error}>{servicesError}</label>
                    )}
                  </div>

                  <div style={{ padding: "1rem" }} />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <TextField
                      label="Message"
                      multiline
                      required
                      fullWidth
                      rows={10}
                      value={message}
                      onChange={(e) => {
                        setMessage(e.target.value);
                        if (message) setMessageError("");
                      }}
                      variant="outlined"
                      style={{
                        color: "#1c4c99",
                      }}
                      InputLabelProps={{
                        style: {
                          color: "#1c4c99",
                        },
                      }}
                      InputProps={{
                        classes: {
                          root: classes.cssOutlinedInput,
                          focused: classes.cssFocused,
                          notchedOutline: classes.notchedOutline,
                        },
                      }}
                    />
                    {messageError && (
                      <label className={classes.error}>{messageError}</label>
                    )}
                  </div>
                  <div
                    className={classes.submitBtn}
                    style={
                      isSubmitButtonDisabled
                        ? {
                            color: "#1c4c99",
                            background: "#f5dd3a",
                            cursor: "not-allowed",
                          }
                        : {}
                    }
                    onClick={!isSubmitButtonDisabled ? handleSubmit : null}
                  >
                    {mailStatus.message}
                  </div>
                </form>
              </Grid>
            </Grid>
          </div>
        </div>
      )}
    </VisibilitySensor>
  );
};

export default ContactContainer;
